import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import api from '../utils/api';
import '../firebase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const cached = localStorage.getItem('horseUser');
    return cached ? JSON.parse(cached) : null;
  });
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchedUid = useRef(null); // 🔁 prevent multiple fetches

  const fetchMongoUser = async (fbUser) => {
    if (!fbUser || fetchedUid.current === fbUser.uid) return;

    try {
      const token = await fbUser.getIdToken(true); // force refresh
      const res = await api.get('/users/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('✅ Mongo user loaded:', res.data);
      setUser(res.data);
      localStorage.setItem('horseUser', JSON.stringify(res.data));
      fetchedUid.current = fbUser.uid; // ✅ mark as fetched
    } catch (err) {
      console.error('❌ Failed to fetch Mongo user:', err.response?.data || err.message);
      setUser(null);
      localStorage.removeItem('horseUser');
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        console.log('👤 Firebase Auth User:', fbUser);
        setFirebaseUser(fbUser);
        fetchMongoUser(fbUser);
      } else {
        console.log('🔓 No Firebase user — clearing session');
        setUser(null);
        localStorage.removeItem('horseUser');
        fetchedUid.current = null;
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    const auth = getAuth();
    const credential = await signInWithEmailAndPassword(auth, email, password);
    setFirebaseUser(credential.user);
    await fetchMongoUser(credential.user);
  };

  const logout = async () => {
    const auth = getAuth();
    await signOut(auth);
    setUser(null);
    localStorage.removeItem('horseUser');
    fetchedUid.current = null;
  };

  return (
    <AuthContext.Provider value={{ user, firebaseUser, loading, login, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);